import { useApiGet } from '@/api/apiRequests';
import { createServiceToken, getServiceTokens, revokeServiceToken } from '@/api/services/serviceTokens';
import { PageLoader } from '@/components/PageLoader';
import type { ServiceTokenCreated, ServiceTokenItem } from '@/interfaces/ServiceTokens';
import { APP_ADMIN_ROLE, hasRole } from '@/utils/roles';
import { AxiosError } from 'axios';
import { Ban, Copy, TriangleAlert } from 'lucide-react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { FC, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

const API_BASE = import.meta.env.VITE_API_URL as string;

const formatDate = (value: string | null): string => (value ? new Date(value).toLocaleString() : '—');

/** Extract a readable message from an API error (custom {detail: string} or FastAPI validation list). */
const errorMessage = (error: unknown, fallback: string): string => {
  const detail = (error as AxiosError<{ detail?: unknown }>)?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((item: { msg?: string }) => item.msg ?? '').join('; ');
  return fallback;
};

export const ApiTokens: FC = () => {
  const isAdmin = useMemo(() => hasRole(APP_ADMIN_ROLE), []);

  const [createDialog, setCreateDialog] = useState<boolean>(false);
  const [newName, setNewName] = useState<string>('');
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [createdToken, setCreatedToken] = useState<ServiceTokenCreated | null>(null);

  const [revokeTarget, setRevokeTarget] = useState<ServiceTokenItem | null>(null);
  const [isRevoking, setIsRevoking] = useState<boolean>(false);

  const {
    data: tokens,
    isLoading,
    refetch,
  } = useApiGet<ServiceTokenItem[], AxiosError>(['service-tokens'], getServiceTokens, { enabled: true });

  const rows = useMemo(() => tokens ?? [], [tokens]);

  const openCreate = () => {
    setNewName('');
    setCreateDialog(true);
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) {
      toast.error('Token name is required.');
      return;
    }
    setIsCreating(true);
    try {
      const created = await createServiceToken(name);
      setCreateDialog(false);
      setCreatedToken(created);
      await refetch();
    } catch (error) {
      toast.error(`Error: ${errorMessage(error, 'Error when creating the token.')}`);
    } finally {
      setIsCreating(false);
    }
  };

  const copyToken = async () => {
    if (!createdToken) return;
    try {
      await navigator.clipboard.writeText(createdToken.token);
      toast.success('Token copied to clipboard.');
    } catch {
      toast.error('Could not copy, select the value and copy it manually.');
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setIsRevoking(true);
    try {
      await revokeServiceToken(revokeTarget.id);
      toast.success(`Token ${revokeTarget.name} has been revoked.`);
      setRevokeTarget(null);
      await refetch();
    } catch (error) {
      toast.error(`Error: ${errorMessage(error, 'Error when revoking the token.')}`);
    } finally {
      setIsRevoking(false);
    }
  };

  const statusBody = (token: ServiceTokenItem) =>
    token.revoked_at ? (
      <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
        revoked {formatDate(token.revoked_at)}
        {token.revoked_by ? ` by ${token.revoked_by}` : ''}
      </span>
    ) : (
      <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">active</span>
    );

  const actionBody = (token: ServiceTokenItem) =>
    token.revoked_at ? null : (
      <Button
        icon={<Ban size={16} />}
        className="p-button-text p-button-danger"
        tooltip="Revoke"
        tooltipOptions={{ position: 'top' }}
        onClick={() => setRevokeTarget(token)}
      />
    );

  return (
    <>
      <h1 className={'text-2xl font-bold mb-8 text-white'}>API</h1>
      <div className="mb-6 p-4 bg-white rounded-md text-sm">
        <p className="mb-2">
          External applications read metric data through the connector API using a service token in the{' '}
          <code className="font-mono">Authorization: Bearer &lt;token&gt;</code> header.
        </p>
        <ul className="list-disc pl-6 font-mono text-xs">
          <li>{API_BASE}/connector/metrics</li>
          <li>{API_BASE}/connector/orps</li>
          <li>{API_BASE}/connector/values?ico=…&amp;metric_code=…&amp;year_from=…&amp;page=1&amp;size=100</li>
          <li>{API_BASE}/connector/values/counts?group_by=ico|metric_code|year</li>
        </ul>
        <p className="mt-2">
          Full description in the{' '}
          <a href={`${API_BASE}/docs`} target="_blank" rel="noreferrer" className="underline text-[#14A4BC]">
            OpenAPI documentation
          </a>
          . Token values are stored only as a hash and are shown once, right after creation.
        </p>
      </div>
      <div className={'flex justify-end gap-2 mb-2'}>
        {isAdmin && (
          <Button label="Add token" className={'btn-primary'} style={{ borderRadius: '6px' }} onClick={openCreate} />
        )}
      </div>
      <DataTable
        value={rows}
        dataKey="id"
        removableSort={true}
        showGridlines={true}
        paginator={true}
        size={'small'}
        sortField={'created_at'}
        sortOrder={-1}
        rows={20}
        emptyMessage="No service tokens yet."
        tableStyle={{ minWidth: '50rem', width: '100%' }}
      >
        <Column field="name" header="Name" sortable={true} />
        <Column
          field="token_prefix"
          header="Token"
          body={(token: ServiceTokenItem) => <span className="font-mono">{token.token_prefix}…</span>}
        />
        <Column
          field="created_at"
          header="Created"
          sortable={true}
          body={(token: ServiceTokenItem) =>
            `${formatDate(token.created_at)}${token.created_by ? ` by ${token.created_by}` : ''}`
          }
        />
        <Column
          field="last_used_at"
          header="Last used"
          sortable={true}
          body={(token: ServiceTokenItem) => formatDate(token.last_used_at)}
        />
        <Column field="revoked_at" header="Status" sortable={true} body={statusBody} />
        {isAdmin && <Column align={'center'} style={{ width: '4rem' }} body={actionBody} />}
      </DataTable>

      <Dialog
        visible={createDialog}
        style={{ width: '28rem' }}
        header="Add service token"
        modal={true}
        className="p-fluid"
        draggable={false}
        onHide={() => setCreateDialog(false)}
        footer={
          <>
            <Button
              className={'btn-secondary'}
              style={{ borderRadius: '6px' }}
              label="Cancel"
              outlined={true}
              onClick={() => setCreateDialog(false)}
              disabled={isCreating}
            />
            <Button
              className={'btn-primary'}
              style={{ borderRadius: '6px' }}
              label="Create"
              onClick={handleCreate}
              loading={isCreating}
            />
          </>
        }
      >
        <div className="field">
          <label htmlFor="token_name" className="font-bold">
            Name
          </label>
          <InputText
            id="token_name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            autoFocus={true}
            placeholder="e.g. powerbi-reporting"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate();
            }}
          />
          <small className="text-gray-500">Identifies the consumer. A revoked token's name can be reused.</small>
        </div>
      </Dialog>

      <Dialog
        visible={createdToken !== null}
        style={{ width: '36rem' }}
        header={`Token ${createdToken?.name ?? ''} created`}
        modal={true}
        draggable={false}
        closable={false}
        onHide={() => setCreatedToken(null)}
        footer={
          <Button
            className={'btn-primary'}
            style={{ borderRadius: '6px' }}
            label="I have stored the token"
            onClick={() => setCreatedToken(null)}
          />
        }
      >
        {createdToken && (
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-3">
              <TriangleAlert size={28} className="text-yellow-600 shrink-0" />
              <p>
                Copy the token now. For security it is stored only as a hash and{' '}
                <strong>cannot be displayed again</strong>. If you lose it, revoke it and create a new one.
              </p>
            </div>
            <div className="flex gap-2 items-center">
              <InputText value={createdToken.token} readOnly={true} className="font-mono text-sm w-full" />
              <Button
                icon={<Copy size={16} />}
                className="btn-secondary"
                style={{ borderRadius: '6px' }}
                tooltip="Copy"
                tooltipOptions={{ position: 'top' }}
                onClick={copyToken}
              />
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        visible={revokeTarget !== null}
        style={{ width: '30rem' }}
        header="Revoke token"
        modal={true}
        draggable={false}
        onHide={() => setRevokeTarget(null)}
        footer={
          <>
            <Button
              label="Cancel"
              className={'btn-secondary'}
              style={{ borderRadius: '6px' }}
              onClick={() => setRevokeTarget(null)}
              disabled={isRevoking}
            />
            <Button
              label="Revoke"
              severity="danger"
              style={{ borderRadius: '6px' }}
              onClick={handleRevoke}
              loading={isRevoking}
            />
          </>
        }
      >
        {revokeTarget && (
          <div className="flex items-start gap-3">
            <TriangleAlert size={28} className="text-red-600 shrink-0" />
            <p>
              Token <strong>{revokeTarget.name}</strong> (<span className="font-mono">{revokeTarget.token_prefix}…</span>)
              will stop working immediately for every application that uses it. This cannot be undone.
            </p>
          </div>
        )}
      </Dialog>
      <PageLoader visible={isLoading} />
    </>
  );
};
